import React, { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { Box, Button, ButtonGroup, CircularProgress } from '@mui/material';

import usePageMeta from 'hooks/use-page-meta';
import Layout from 'pages/Search/components/Layout';
import Paginator from 'pages/Search/components/Paginator';
import SectionHeading from 'pages/Search/components/SectionHeading';
import { useTypesCveCatalogPayload } from 'pages/Search/components/TypesCveList/hooks/use-types-cve-catalog-payload';
import { useTypesCveCatalogState } from 'pages/Search/components/TypesCveList/hooks/use-types-cve-catalog-state';
import VulnerabilitiesList from 'pages/Search/components/VulnerabilitiesList';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';
import { useSearch } from 'queries/search/use-search';

import { Order, TYPES_CVE_LIST_PAGE_SIZE, TYPES_CVE_MOST_VIEWED_LIMIT } from './constants';
import useStyles from './styles';

const TypesCveList: React.FC = () => {
    const { classes, cx } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [state, actions] = useTypesCveCatalogState();
    const payload = useTypesCveCatalogPayload(state);
    const { data, isLoading } = useSearch(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const typeName = normalizeName(state.type);
    const hasMetaPagination = state.page > 1;
    const isMostViewed = state.order === Order.MOST_VIEWED;

    usePageMeta({
        title: hasMetaPagination
            ? isMostViewed
                ? `${typeName} - Page ${state.page} of ${typeName} Most Viewed Vulnerabilities List | Vulners.com`
                : `${typeName} - Page ${state.page} of ${typeName} Vulnerabilities List | Vulners.com`
            : isMostViewed
            ? `${typeName} - Most Viewed Vulnerabilities List | Vulners.com`
            : `${typeName} - Vulnerabilities List | Vulners.com`,
        description: hasMetaPagination
            ? isMostViewed
                ? `Most Viewed Security Vulnerabilities published by ${typeName} - Page ${state.page}`
                : `Security Vulnerabilities published by ${typeName} - Page ${state.page}`
            : isMostViewed
            ? `Most Viewed Security Vulnerabilities published by ${typeName}`
            : `Security Vulnerabilities published by ${typeName}`
    });

    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);
    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);

    const hasPagination = data ? data.total > payload.size : true;

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={[
                            typeName,
                            state.order === Order.MOST_VIEWED ? 'Most viewed' : null,
                            state.order === Order.DATE ? 'Recent' : null
                        ]}
                        breadcrumbs={[{ to: ROUTES.TYPES_CVE({ type: state.type }), label: typeName }]}
                        actions={
                            <ButtonGroup disableElevation disableFocusRipple variant="contained">
                                <Button
                                    component={Link}
                                    to={actions.url({ order: Order.DATE })}
                                    className={cx(classes.button, { [classes._active]: state.order === Order.DATE })}
                                >
                                    {'Recent'}
                                </Button>
                                <Button
                                    component={Link}
                                    to={actions.url({ order: Order.MOST_VIEWED })}
                                    className={cx(classes.button, {
                                        [classes._active]: state.order === Order.MOST_VIEWED
                                    })}
                                >
                                    {'Most viewed'}
                                </Button>
                            </ButtonGroup>
                        }
                    />
                    {isLoading ? (
                        <Box className={classes.spinner}>
                            <CircularProgress />
                        </Box>
                    ) : data ? (
                        <>
                            <VulnerabilitiesList total={data.total} items={data.search} exact={data.exactMatch} />
                            {hasPagination ? (
                                <Paginator
                                    totalCountLabel={'Total number of security vulnerabilities'}
                                    page={state.page}
                                    rowsPerPage={TYPES_CVE_LIST_PAGE_SIZE}
                                    rowsPerPageOptions={null}
                                    count={
                                        isMostViewed ? Math.min(TYPES_CVE_MOST_VIEWED_LIMIT, data.total) : data.total
                                    }
                                    onPageChange={handlePageChange}
                                    getPaginationHref={getPaginationHref}
                                />
                            ) : null}
                        </>
                    ) : null}
                </div>
            }
        />
    );
};

export default TypesCveList;
